import React, {useEffect, useState} from 'react';
import styled, {useTheme} from 'styled-components/native';
import {isWeb, wdp} from '../../../utils/responsive';
import Icon from '../../../components/icon';
import {Theme} from '../../../theme/lightTheme';
import {ProfileScreenProps} from '../../../interfaces/screens/profileScreen';
import Animated, {FadeIn, FadeOut} from 'react-native-reanimated';
import {tr} from '../../../translations';
import {useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import {View, Keyboard, TouchableOpacity} from 'react-native';
import DatePicker from '../../../components/datePicker';
import EditItem from '../../../components/editItem';
import {handleChange} from '../../../utils/form';
import Picker from '../../../components/picker';
import {openCountryPickerModal} from '../../../connected-components/countryPickerModal/actions';
import {convertToYYYYMMDD, getUserDetailsByIds} from '../../../utils/helpers';
import dayjs from 'dayjs';
import {updateUser} from './actions';
import {openUploadModal} from '../../../connected-components/uploadModal/actions';
import TextGeneric from '../../../components/textGeneric';
import ProgressiveImage from '../../../components/progressiveImage';
import {BottomSheetScrollView} from '@gorhom/bottom-sheet';
import {ScrollView} from 'react-native-gesture-handler';
import useResponsiveDimensions from '../../../utils/hooks/useResponsiveDimensions';
import {getSpecialities} from '../home/actions';
import {SpecialityType} from '../../../interfaces/api/auth/login';
import {Platform} from 'react-native';
import '../../../utils/webAnimations';

const Container = styled.View`
  flex: 1;
  width: 90%;
  align-self: center;
  padding-top: 20px;
`;
const Title = styled(TextGeneric)`
  align-self: center;
  font-size: ${({theme}) => theme.text.s6}px;
  color: ${({theme}) => theme.text.dark};
  text-transform: capitalize;
`;
const MainView = styled(Animated.View)`
  padding-top: 10%;
`;
const ImageContainer = styled.View`
  height: ${({theme}) => theme.text.s1 * 2.5}px;
  aspect-ratio: 1;
  border-radius: ${({theme}) => theme.text.s1 * 2.5}px;
  align-self: center;
  background-color: ${({theme}) => theme.border};
  border-color: ${({theme}) => theme.settings.icon + 'CC'};
  border-width: 4px;
`;
const ProfileImage = styled(ProgressiveImage)`
  height: 100%;
  width: 100%;
  border-radius: ${wdp(15)}px;
`;
const TopContainer = styled(TouchableOpacity)`
  width: ${wdp(30)}px;
  margin-vertical: 5%;
  align-self: center;
`;
const Separator = styled.View`
  width: ${wdp(100)}px;
  align-self: center;
  height: 1px;
  background-color: ${({theme}) => theme.primary + '22'};
`;
const EditIconContainer = styled.View`
  background-color: ${({theme}) => theme.settings.icon};
  padding: 5%;
  align-items: center;
  justify-content: center;
  display: flex;
  position: absolute;
  right: -${({theme}) => theme.text.s55 / 2}px;
  top: -${({theme}) => theme.text.s55 / 3}px;
  border-radius: 12px;
  border-width: 2px;
  border-color: ${({theme}) => theme.settings.icon};
  opacity: 0.9;
`;

const mandatoryFields = [
  'firstName',
  'lastName',
  'bod',
  'country',
  'gender',
  'specialty',
];
const ProfilePage: React.FC<ProfileScreenProps> = () => {
  const theme = useTheme() as Theme;
  const {hdp, isMobile} = useResponsiveDimensions();
  const [_forceRender, setForceRender] = useState(0);

  const {currentUser, config} = useSelector(
    (state: RootState) => state.auth,
  ) || {
    currentUser: {},
  };
  const {genders, languages, professions, specialties} = config || {};
  const {
    gender_id,
    profession_id,
    country_id,
    language_id,
    specialty_id,
    id,
    email,
    first_name,
    last_name,
    date_of_birth,
  } = currentUser || {};
  const {gender, country, profession, specialty, language} =
    getUserDetailsByIds({
      config,
      country_id,
      gender_id,
      profession_id,
      language_id,
      specialty_id,
    });
  const [localSpecialties, setLocalSpecialties] = useState<SpecialityType[]>(
    specialties || [],
  );
  const [isEdited, setIsEdited] = useState(false);

  // Define the form state
  const [form, updateForm] = useState<any>({
    email: email || '',
    firstName: first_name || '',
    lastName: last_name || '',
    bod: convertToYYYYMMDD(date_of_birth || '') ?? '',
    gender: gender || '',
    country: country || '',
    profession: profession,
    specialty: specialty || '',
    language: language || '',
  });

  // Define the error state
  const [image, setImage] = useState<{
    uri?: string;
    name: string;
    type: string;
  }>();

  // Add useEffect to trigger a re-render when component mounts for bottom sheet to scroll
  useEffect(() => {
    setTimeout(() => {
      setForceRender(prev => prev + 1);
    }, 0);
  }, []);

  // Function to handle form submission
  const submit = async (formData?: any) => {
    Keyboard.dismiss();
    const currentForm = formData || form;
    const {
      firstName,
      lastName,
      bod,
      gender,
      country,
      profession,
      specialty,
      language,
    } = currentForm;

    const body: any = {
      id,
      first_name: firstName,
      last_name: lastName,
      date_of_birth: dayjs(bod).format('YYYY-MM-DD'),
      gender_id: gender?.id,
      country_id: country?.id,
      profession_id: profession?.id,
      specialty_id: specialty?.id || 1,
      language_id: language?.id || 2,
      image,
    };

    await updateUser(body);
  };

  // Professional helper function to update form and submit
  const updateAndSubmit = (key: string, value: any, shouldSubmit: boolean = true) => {
    updateForm((prevForm: any) => {
      const newForm = {
        ...prevForm,
        [key]: value,
      };

      // Submit with the new form data after state update
      if (shouldSubmit) {
        // Use setTimeout to ensure state update is applied
        setTimeout(() => submit(newForm), 0);
      }

      return newForm;
    });
  };

  const uploadImage = () =>
    openUploadModal({
      onSelectionChange: (file: any) => {
        if (file) {
          setImage(file);
          submit();
        }
      },
      options: {
        mediaType: 'photo',
      },
    });

  const _getPickerList = (label: string): any[] => {
    switch (label) {
      case 'gender':
        return genders || [];
      case 'language':
        return languages || [];
      case 'profession':
        return professions || [];
      case 'specialty':
        return localSpecialties || [];
      default:
        return [];
    }
  };

  const getObjectByName = (label: string, name: string | number): any => {
    const data = _getPickerList(label);
    const idx = data.findIndex(i => i.name === name);
    if (idx > -1) {
      return data[idx];
    }
    return {};
  };

  const updateSpecialities = async () => {
    if (form?.profession?.id) {
      const res = await getSpecialities(form?.profession?.id);
      setLocalSpecialties(res);
      handleChange('specialty', res?.[0] || null, updateForm);
      setIsEdited(true);
    }
  };

  useEffect(() => {
    if (form?.profession?.id !== currentUser?.profession_id) {
      updateSpecialities();
    }
  }, [form.profession]);

  useEffect(() => {
    if (isEdited) {
      submit(form);
      setIsEdited(false);
    }
  }, [form.specialty, isEdited]);

  const _renderScreen = () => {
    return (
      <>
        <TopContainer onPress={uploadImage}>
          <ImageContainer>
            <ProfileImage
              defaultImage={require('../../../assets/images/user.png')}
              source={{
                uri: image?.uri ? image.uri : currentUser?.image_url,
              }}
            />
            <EditIconContainer>
              <Icon
                name={'edit'}
                color={theme.homeBackground}
                size={theme.text.s55}
              />
            </EditIconContainer>
          </ImageContainer>
        </TopContainer>
        <Title>{currentUser?.first_name + ' ' + currentUser?.last_name}</Title>
        <MainView
          {...(Platform.OS === 'web'
            ? {className: 'screen-fade-in'}
            : {entering: FadeIn, exiting: FadeOut})}>
          {mandatoryFields.map(label => {
            if (label === 'bod') {
              return (
                <View key={label}>
                  <Separator />
                  <DatePicker
                    placeholder={tr(`register.${label}`)}
                    maxDate={new Date().toISOString()}
                    value={form[label] || ''}
                    style={{
                      marginBottom: hdp(2),
                    }}
                    customComponent={() => {
                      return (
                        <EditItem
                          disableEdit
                          label={tr(`register.${label}`)}
                          value={form[label] || ''}
                          rightEditIconName={'calendar'}
                        />
                      );
                    }}
                    label={''}
                    onValueChange={text => {
                      updateAndSubmit(label, text);
                    }}
                    //@ts-ignore
                    maximumDate={new Date()}
                    format={'YYYY-MM-DD'}
                  />
                </View>
              );
            }
            if (
              label === 'gender' ||
              label === 'language' ||
              label === 'profession' ||
              label === 'specialty'
            ) {
              return (
                <View key={label}>
                  <Separator />
                  <Picker
                    key={label}
                    style={{
                      marginBottom: hdp(2),
                    }}
                    customComponent={() => {
                      return (
                        <EditItem
                          isPicker
                          label={tr(`register.${label}`)}
                          value={form[label] ? form[label].name : '--'}
                        />
                      );
                    }}
                    list={_getPickerList(label).map(i => i?.name || '')}
                    value={form[label] ? form[label].name : ''}
                    label={''}
                    onValueChange={val => {
                      const value = getObjectByName(label, val);
                      if (label === 'specialty') {
                        handleChange(label, value, updateForm);
                        setIsEdited(true);
                      } else if (label !== 'profession') {
                        updateAndSubmit(label, value);
                      } else {
                        // Profession doesn't auto-submit
                        handleChange(label, value, updateForm);
                      }
                    }}
                  />
                </View>
              );
            }
            if (label === 'country') {
              return (
                <View key={label}>
                  <Separator />
                  <EditItem
                    key={label}
                    label={tr(`register.${label}`)}
                    value={form[label] ? form[label].name : ''}
                    onChangeText={text => handleChange(label, text, updateForm)}
                    style={{
                      marginBottom: hdp(2),
                    }}
                    onPress={() => {
                      openCountryPickerModal({
                        onSelectionChange: country => {
                          updateAndSubmit(label, country);
                        },
                        currentValue: form[label] ? form[label].name : '',
                      });
                    }}
                    onSavePress={submit}
                  />
                </View>
              );
            }
            return (
              <View key={label}>
                <Separator />
                <EditItem
                  key={label}
                  label={tr(`register.${label}`)}
                  value={form[label]}
                  onChangeText={text => handleChange(label, text, updateForm)}
                  style={{
                    marginBottom: hdp(2),
                  }}
                  onSavePress={submit}
                />
              </View>
            );
          })}
        </MainView>
      </>
    );
  };

  const BottomSheetScroll =
    isWeb && !isMobile ? ScrollView : BottomSheetScrollView;

  return (
    <BottomSheetScroll
      contentContainerStyle={{
        flexGrow: 1,
      }}
      keyboardShouldPersistTaps="handled">
      <Container style={[isWeb && {minHeight: hdp(80)}]}>
        {_renderScreen()}
      </Container>
    </BottomSheetScroll>
  );
};
export default ProfilePage;
